import { useCallback } from 'react'

import dayjs from 'dayjs'
import excelJs from 'exceljs'

import { useDownloadGeneralContractorOrderXlsmStore } from 'src/hooks/stores'
import { useToast } from 'src/hooks/useToast'
import {
  RoomApplicationEntity,
  RoomEntity,
  RoomSelectRegistrationEntity,
  SelectMasterEntity,
  WorkflowEntity,
} from 'src/types'

type ShowDownloadDataInput = {
  projectId: string
  applicationId: string
}

type CreateDownloadDataInput = {
  projectId: string
  projectName: string
  rooms: RoomEntity[]
  selectMasters: SelectMasterEntity[]
  allRoomSelectRegistrations: RoomSelectRegistrationEntity[]
  allApplications: RoomApplicationEntity[]
  allWorkflows: WorkflowEntity[]
}

export const useDownloadGeneralContractorOrderXlsm = () => {
  const {
    downloadGeneralContractorOrderXlsmStoreValue,
    setDownloadGeneralContractorOrderXlsmStoreValue,
  } = useDownloadGeneralContractorOrderXlsmStore()
  const { displayErrorTextToast } = useToast()

  const showDownloadGeneralContractorOrder = (input: ShowDownloadDataInput) => {
    const { ...others } = input

    setDownloadGeneralContractorOrderXlsmStoreValue({
      isShow: true,
      ...others,
    })
  }

  const hideDownloadGeneralContractorOrder = () => {
    setDownloadGeneralContractorOrderXlsmStoreValue(previous => ({
      ...previous,
      isShow: false,
      projectId: undefined,
      managerName: undefined,
      projectName: undefined,
      applicationId: undefined,
      targetRooms: undefined,
      targetSelectMasters: undefined,
    }))
  }

  // 数字に該当するアルファベットを取得する1 → A, 2 → B, 3 → C, ...26 -> Z, 27 -> AA, ...
  const getAlphabetCharacterFromNumber = (n: number): string | undefined => {
    // アルファベットの最初の文字 'A' の文字コードを取得
    const codeOfA = 'A'.codePointAt(0)
    if (!codeOfA) {
      return undefined
    }

    let number = n
    let columnLabel = ''
    while (number > 0) {
      // 0から始まるインデックスに調整
      number -= 1
      const remainder = number % 26
      // 対応する文字コードを計算してラベルに追加
      columnLabel = String.fromCodePoint(codeOfA + remainder) + columnLabel
      // 次の桁へ
      number = Math.floor(number / 26)
    }

    return columnLabel
  }

  // 列を数値に変換する A -> 1, B -> 2, C -> 3, ... Z -> 26, AA -> 27, ...
  const getNumberFromAlphabetCharacter = (column: string) => {
    return [...column].reduce((accumulator, char) => {
      const charCode = char.codePointAt(0)
      if (charCode) {
        return 26 * accumulator + charCode - 'A'.codePointAt(0)! + 1
      }
      return accumulator
    }, 0)
  }

  // 2つの文字列から範囲内のセルの配列を取得する。例：A1, B2 → [A1, A2, B1, B2]
  const getCellRange = (start: string, end: string): string[] => {
    const range: string[] = []

    // 列（アルファベット部分）と行（数字部分）を分離
    const startColumnAlphabet = start.match(/[a-z]+/i)![0]
    const endColumnAlphabet = end.match(/[a-z]+/i)![0]
    const startRowNumber = Number.parseInt(start.match(/\d+/)![0])
    const endRowNumber = Number.parseInt(end.match(/\d+/)![0])

    // 列の数値範囲を計算
    const startColumnNumber = getNumberFromAlphabetCharacter(startColumnAlphabet.toUpperCase())
    const endColumnNumber = getNumberFromAlphabetCharacter(endColumnAlphabet.toUpperCase())

    // 列の範囲を計算
    for (let col = startColumnNumber ?? 0; col <= endColumnNumber; col += 1) {
      // 行の範囲を計算
      for (let row = startRowNumber; row <= endRowNumber; row += 1) {
        // 範囲内の各セルを配列に追加
        const colLabel = getAlphabetCharacterFromNumber(col)
        if (colLabel) {
          range.push(colLabel + row)
        }
      }
    }

    return range
  }

  const createCellData = (
    room: RoomEntity,
    selectMaster: SelectMasterEntity,
    roomSelectRegistrations: RoomSelectRegistrationEntity[]
  ) => {
    // 選択されたオプションを取得
    const targetRoomSelectRegistration = roomSelectRegistrations.find(r => r.id === room.id)
    const targetSelectRegistration = targetRoomSelectRegistration?.selects.find(
      s => s.selectMasterId === selectMaster.id
    )
    const option = targetSelectRegistration?.registeredSelectOption

    let name = option?.name ?? ''
    if (option?.isPaid) {
      name = `${name}（有償）`
    }

    // 子回答がある場合もその旨を記載
    if (option?.child) {
      name = `${name}/${option.child.name}`
      if (option.child.isPaid) {
        name = `${name}（有償）`
      }
    }

    return name
  }

  const loadTargetOrderApplications = useCallback(
    (input: {
      targetRooms: RoomEntity[]
      allApplications: RoomApplicationEntity[]
      allWorkflows: WorkflowEntity[]
    }) => {
      const { targetRooms, allApplications, allWorkflows } = input
      // 対象住戸を含む申請（公開申請も含む）
      const targetApplications = allApplications.filter(a =>
        targetRooms.some(r => a.roomIds.includes(r.id))
      )
      // 対象となる発注申請
      return targetApplications.filter(a => {
        const targetWorkflow = allWorkflows.find(w => w.id === a.workflowId)
        return a.status === 'approved' && targetWorkflow?.type === 'order'
      })
    },
    []
  )

  // 対象住戸を元にゼネコン発注申請用のエクセルデータを作成する
  const createDownloadData = async (input: CreateDownloadDataInput) => {
    const {
      projectName,
      selectMasters,
      rooms: targetRooms,
      allRoomSelectRegistrations,
      allApplications,
      allWorkflows,
    } = input

    // 対象となる発注申請
    const targetOrderApplications = loadTargetOrderApplications({
      targetRooms,
      allApplications,
      allWorkflows,
    })

    if (targetOrderApplications.length === 0) {
      displayErrorTextToast('ダウンロード時に予期せぬエラーが発生しました')
      return
    }

    // 間取りセレクトのある/なしによってセルの表示を切り替える必要がある
    const planSelectSelectMasters = selectMasters.filter(s => s.isPlanSelect)
    const nonPlanSelectSelectMasters = selectMasters.filter(s => !s.isPlanSelect)
    const hasPlanSelect = planSelectSelectMasters.length > 0

    const workbook = new excelJs.Workbook()

    // 対象となる発注申請ごとにシートを作成する
    targetOrderApplications.map(targetOrderApplication => {
      workbook.addWorksheet(targetOrderApplication.id)
      const worksheet = workbook.getWorksheet(targetOrderApplication.id)

      if (!worksheet) {
        return
      }

      // 発注申請の最初のステップがチーフとなり、そのステップでの承認者を表示する
      // 代理承認を行う場合、確認者（confirmations）は複数人になるため、statusがapprovedの承認を行った人を表示する
      const confirmation = targetOrderApplication.steps[0].confirmations.find(
        c => c.status === 'approved'
      )

      // 検印依頼日は発注申請を行った日
      const date = dayjs(targetOrderApplication.createdAt).format('YYYY年MM月DD日')
      // eslint-disable-next-line no-irregular-whitespace
      const dateTxt = `検印依頼日： ${date} / ゼネコン発注日： 年　月　日`
      // 代理承認の場合はその旨を表示する
      worksheet.addRow([confirmation?.isProxy ? '代理承認' : 'チーフ', dateTxt])

      // ゼネコン発注申請Noはユニークな番号を設定するため、申請IDを使用
      const title = `ゼネコン発注申請No: ${targetOrderApplication.id}\n物件名： ${projectName}`

      worksheet.addRow([
        confirmation?.verifier?.name ??
          confirmation?.verifier?.email ??
          `${confirmation?.verifierId}（削除済）`,
        title,
      ])

      worksheet.addRow([])
      worksheet.addRow([])

      // 間取りセレクトがONの設問は、4列目に間取りセレクトとしてまとめて表示する必要がある
      const headCellTitles = hasPlanSelect
        ? [
            'No',
            '住戸番号',
            '住戸タイプ',
            '間取りセレクト',
            ...nonPlanSelectSelectMasters.map(s => s.name),
            'セレクト対象外',
            '検印後編集住戸',
            '営業編集履歴',
          ]
        : [
            'No',
            '住戸番号',
            '住戸タイプ',
            ...selectMasters.map(s => s.name),
            'セレクト対象外',
            '検印後編集住戸',
            '営業編集履歴',
          ]
      worksheet.addRow(headCellTitles)

      // シートに出力する対象の住戸
      const sheetRooms = targetRooms.filter(r => targetOrderApplication.roomIds.includes(r.id))
      for (const [index, room] of sheetRooms.entries()) {
        const row = [
          `${index + 1}`, // No
          room.name, // 住戸番号
          room.roomType?.name, // 住戸タイプ
        ]

        // 間取りセレクトがONの設問は、1つにまとめて表示する
        if (hasPlanSelect) {
          const planSelectCell = planSelectSelectMasters
            .map(selectMaster => createCellData(room, selectMaster, allRoomSelectRegistrations))
            // 空文字（選択していない設問）は除外
            .filter(s => s !== '')
            .join('・')
          row.push(planSelectCell || '-')
        }

        // 間取りセレクトがOFFの設問は、1つ1つ表示する
        const nonPlanSelectCells = nonPlanSelectSelectMasters.map(
          selectMaster => createCellData(room, selectMaster, allRoomSelectRegistrations) || '-'
        )
        row.push(
          ...nonPlanSelectCells,
          // セレクト対象外の場合は◯を出力
          room.isSelectEnabled ? '' : '◯',
          // 検印後編集住戸（発注申請承認取下げを行った住戸）の場合は◯を出力
          room.orderApplicationCanceledAt ? '◯' : '',
          // 営業編集履歴（発注申請承認取下げ時にコメントをした住戸）がある場合は◯を出力
          room.orderApplicationCancelComment ?? ''
        )
        worksheet.addRow(row)
      }

      // 内訳: チーフ名やタイトルを表示する2行、空白の2行、セレクト内容のタイトル等を表示する1行、出力する住戸数分の行
      const totalRowCount = 5 + sheetRooms.length
      for (const row of worksheet.getRows(1, totalRowCount) ?? []) {
        row.alignment = { vertical: 'middle', horizontal: 'left' }
        row.font = { size: 11 }
      }

      // タイトル周りのセルをマージ
      worksheet.mergeCells('B1:D1')
      worksheet.mergeCells('B2:D2')
      // タイトル周りのセルのスタイルを設定
      worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' }
      worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' }
      worksheet.getCell('B1').alignment = { vertical: 'middle', horizontal: 'left' }
      worksheet.getCell('B2').alignment = { vertical: 'middle', horizontal: 'left' }
      // 2行目は高さを高くする
      worksheet.getRow(2).height = 60
      // タイトル周りのボーダー
      for (const cell of getCellRange('A1', 'D2') ?? []) {
        worksheet.getCell(cell).border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        }
      }

      // セレクト内容を表示するテーブル部分の最後の列のアルファベットを取得
      // セレクト内容数 + 6（No, 住戸番号, 住戸タイプ, セレクト対象外, 検印後編集住戸, 営業編集履歴）
      const selectTableLastAlphabet = getAlphabetCharacterFromNumber(selectMasters.length + 6)
      const selectTableStartRowNumber = 5
      const selectTableStartCol = `A${selectTableStartRowNumber}`

      // セレクト内容を表示するテーブルのタイトル行には背景色を設定
      const fillEndCol = selectTableLastAlphabet
        ? `${selectTableLastAlphabet}5`
        : selectTableStartCol
      for (const col of getCellRange(selectTableStartCol, fillEndCol)) {
        worksheet.getCell(col).fill = {
          type: 'pattern',
          pattern: 'darkVertical',
          fgColor: { argb: 'FFCCCCCC' },
        }
      }
      // セレクト内容を表示するテーブルのタイトル行は文字を折り返す
      worksheet.getRow(selectTableStartRowNumber).alignment = {
        wrapText: true,
        vertical: 'middle',
        horizontal: 'left',
      }

      // セレクト内容を表示するテーブルのボーダー
      const borderEndCol = selectTableLastAlphabet
        ? `${selectTableLastAlphabet}${selectTableStartRowNumber + sheetRooms.length}`
        : selectTableStartCol
      for (const col of getCellRange(selectTableStartCol, borderEndCol)) {
        worksheet.getCell(col).border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        }
      }

      // 列幅
      // 最初の列（チーフ名、セレクトテーブルのNo）
      worksheet.getColumn(1).width = 15
      // 住戸番号
      worksheet.getColumn(2).width = 10
      // 住戸タイプ
      worksheet.getColumn(3).width = 10
      // セレクトテーブルの各セレクト内容
      for (let col = 2; col <= selectMasters.length + 3; col += 1) {
        worksheet.getColumn(col).width = 20
      }
      // セレクト対象外
      worksheet.getColumn(selectMasters.length + 4).width = 30
      // 検印後編集住戸
      worksheet.getColumn(selectMasters.length + 5).width = 30
      // 営業編集履歴
      worksheet.getColumn(selectMasters.length + 6).width = 30
      return null
    })

    const uint8Array = await workbook.xlsx.writeBuffer()
    return new Blob([uint8Array], { type: 'application/octet-binary' })
  }

  return {
    isShow: downloadGeneralContractorOrderXlsmStoreValue.isShow,
    targetProjectId: downloadGeneralContractorOrderXlsmStoreValue.projectId,
    showDownloadGeneralContractorOrder,
    hideDownloadGeneralContractorOrder,
    loadTargetOrderApplications,
    createDownloadData,
  }
}
